import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticator } from '@aws-amplify/ui-react';
import useGetCurrentUser from '../../API/hooks/users/useGetCurrentUser';
import useGetVideos from '../../API/hooks/videos/useGetVideos';
import useGetVideosCount from '../../API/hooks/videos/useGetVideosCount';
import useGetVehiclesLocations from '../../API/hooks/maps/useGetVehiclesLocations';
import { useGetVehicleLocationsByIds } from '../../API/hooks/maps/useGetVehicleLocations';
import { FilterConfig } from '../../components/common/DropdownFilter/types';
import ItemsList, { ItemsListRef } from '../../components/common/ItemsList/ItemsList';
import Modal from '../../components/common/Modal/Modal';
import Toolbar from '../../components/common/Toolbar/Toolbar';
import GroupSelection from '../../components/GroupSelection/GroupSelection';
import MapWidget from '../../components/common/Map/Map';
import { ThemeType } from '../../theme/themeType';
import S from './VideosPageStyles';
import VideoItem from './VideoItem/VideoItem';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import { RequestVideo } from './Modals';
import { getEndOfTheDay, getTimeRangeText, getPeriodDisplayLabel } from '../../utils/dateUtils';
import useGetRole from '../../API/hooks/useGetRole';
import { useDeleteVideos } from '../../API/hooks/videos/useDeleteVideo';
import useGetFleetFeatures from '../../API/hooks/useGetFleetFeatures';
import { getLocalStorageKey } from '../../utils/storageUtils';
import { queryFilters } from '../../utils/queryFilters';
import useGetFleet from '../../API/hooks/useGetFleetDetails';
import useGetVideosReport from '../../API/hooks/reports/useGetVideosReport';
import useGetCurrentPage from '../../API/hooks/useGetCurrentPage';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import { videosPageFilters } from '../../constants/FilterDefault';
import useLocalStorage, { useGroupFilters } from '../../API/hooks/useLocalStorage';
import useOngoingTripMapIds from '../../API/hooks/trips/useOngoingTripMapIds';
import {
  buildDeleteVideosRequest,
  canShowVideoDeleteAction,
  findVideosByIds,
} from '../../utils/videoDeleteUtils';

const getVideoQuery = (startDate?: string, endDate?: string): string => (
  `&from=${startDate || new Date(0)}&to=${endDate || getEndOfTheDay().toISOString()}`
);

const SEARCH_CONFIG = [
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { fromTimestamp_DESC: 'Newest first' },
      { fromTimestamp_ASC: 'Oldest first' },
      { updatedAt_DESC: 'Newest uploaded first' },
      { updatedAt_ASC: 'Oldest uploaded first' },
    ],
  },
  {
    type: 'cameraId',
    display: 'radio',
    options: [
      { null: 'All' },
      { 1: 'Outward camera' },
      { 2: 'Inward camera' },
    ],
  },
  {
    type: 'alertId',
    display: 'radio',
    options: [
      { null: 'All' },
      { alert: 'Alert video' },
      { manual: 'Custom VoD' },
    ],
  },
  {
    type: 'status',
    display: 'radio',
    options: [
      { null: 'All' },
      { completed: 'Video present' },
      { startedProcessing: 'Uploading' },
      { pending: 'Queued' },
      { failedToRequest: 'Failure (request)' },
      { failedToUpload: 'Failure (upload)' },
    ],
  },
] as FilterConfig[];

function VideosPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const fleetId = useGetFleetId();
  const { user } = useAuthenticator();
  const { data: currentUser } = useGetCurrentUser(fleetId, user.attributes?.email);
  const queryClient = useQueryClient();
  const { isFleetViewer } = useGetRole();
  const [RequestVideoModalOpen, toggleRequestVideoModal] = useState(false);
  const { data: fleetData } = useGetFleet(fleetId);
  const [groupFilters, setGroupFilters] = useGroupFilters();
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const itemsListRef = useRef<ItemsListRef>(null);
  const [filters, setFilters] = useLocalStorage(getLocalStorageKey('videos-page-filters'), videosPageFilters);
  const closeModal = useCallback(() => toggleRequestVideoModal(false), []);
  const { videoRecording: videoRecordingEnabled } = useGetFleetFeatures();

  const { mutate: mutateDelete } = useDeleteVideos(
    () => {
      toast.success('Video(s) deleted successfully');
      itemsListRef.current?.toggleSelection(false);
    },
    (errorMessage) => toast.error(errorMessage),
  );

  const handleDeleteVideos = (ids: string[]): void => mutateDelete({
    fleetId,
    items: buildDeleteVideosRequest(queryClient, fleetId, ids, currentUser?._id),
  });

  const handleDeleteItems = (): void => {
    const deletableIds = findVideosByIds(queryClient, fleetId, selectedItems)
      .filter((video) => canShowVideoDeleteAction(video, currentUser?._id, isFleetViewer))
      .map((video) => video._id);

    if (deletableIds.length === 0) {
      toast.error('You can only delete videos you requested or alert videos you have access to.');
      toggleDeleteModalOpen(false);
      return;
    }

    if (deletableIds.length < selectedItems.length) {
      toast.error('Some selected videos cannot be deleted and were skipped.');
    }

    handleDeleteVideos(deletableIds);
    toggleDeleteModalOpen(false);
  };

  const BUTTONS = [
    {
      title: 'Request a VOD',
      background: theme.colors.main_cta,
      hoverBackground: theme.colors.white,
      hoverColor: theme.colors.black,
      textColor: theme.colors.black,
      action: () => toggleRequestVideoModal(true),
      hide: isFleetViewer,
      disabled: !videoRecordingEnabled,
    },
  ];

  const { ongoingVehicleIds } = useOngoingTripMapIds(fleetId);

  const countQuery = queryFilters(
    { ...(filters || {}), ...(groupFilters || null) },
    undefined,
    getVideoQuery(filters?.startDate?.[0], filters?.endDate?.[0]),
  );
  const { data: videosCountData } = useGetVideosCount(fleetId, countQuery, !!fleetId);
  const videosCount = videosCountData?.count ?? 0;
  const videosTitle = `${videosCount} ${videosCount === 1 ? 'Video' : 'Videos'} • ${getPeriodDisplayLabel(filters)}`;

  const LIST_BUTTONS = [
    {
      title: `Delete${selectedItems.length > 0 ? ` ${selectedItems.length} video(s)` : ''}`,
      action: () => toggleDeleteModalOpen(true),
      hide: isFleetViewer,
      height: '25px',
      background: theme.colors.red,
      hoverBackground: theme.colors.red,
      disabled: selectedItems.length < 1,
    },
  ];

  return (
    <>
      <S.Wrapper>
        <Toolbar
          timeSelection
          title="Video streams"
          filters={filters}
          setFilters={setFilters}
          buttons={BUTTONS}
          allTimeOption
        />
        <div className="sub-header">
          <GroupSelection filters={groupFilters || {}} setFilters={setGroupFilters} />
        </div>
        <div className="widgets-container">
          <MapWidget
            fleetId={fleetId}
            entityType="video"
            getLocationsQuery={useGetVehiclesLocations}
            getLocationDetailsQuery={useGetVehicleLocationsByIds}
            groupFilters={groupFilters || {}}
            fleetOngoingVehicleIds={ongoingVehicleIds}
          />
        </div>
        <ItemsList
          ref={itemsListRef}
          entity="videos"
          title={videosTitle}
          fleetId={fleetId}
          customQuery={getVideoQuery(filters?.startDate?.[0], filters?.endDate?.[0])}
          customPageLimit={21}
          hasCreateReport
          hasSearch
          reportEndpoint={useGetVideosReport}
          reportName={`${fleetData?.name || 'fleet_na'}_videos_${getTimeRangeText(filters)}`}
          filtersConfig={SEARCH_CONFIG}
          filters={filters}
          defaultFilters={videosPageFilters}
          setFilters={setFilters}
          groupFilters={groupFilters || {}}
          getQuery={useGetVideos}
          buttons={LIST_BUTTONS}
          onSelect={setSelectedItems}
          isRow
          curentPage={useGetCurrentPage()}
        >
          <VideoItem />
        </ItemsList>
      </S.Wrapper>
      {RequestVideoModalOpen && (
        <Modal
          title="Request a VOD"
          state={RequestVideoModalOpen}
          toggleState={toggleRequestVideoModal}
          settings={{
            maxHeight: '90vh',
            marginTop: '8px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <RequestVideo fleetId={fleetId} closeModal={closeModal} />
        </Modal>
      )}
      {deleteModalOpen && (
        <Modal
          title="Confirm delete"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
          settings={{
            minWidth: '500px',
          }}
        >
          <DeleteModal
            title="Are you sure you want to delete these video(s)"
            onClose={() => toggleDeleteModalOpen(false)}
            onDelete={handleDeleteItems}
          />
        </Modal>
      )}
    </>
  );
}

export default VideosPage;
